/**
 * Utility functions for API key management and authentication
 */

/**
 * Get API key from session storage
 * @returns {string|null} API key or null if not found
 */
export const getApiKey = () => {
  try {
    const userLogin = JSON.parse(sessionStorage.getItem("userLogin") || "[]");
    return userLogin.length > 0 ? userLogin[0].apiKey : null;
  } catch (error) {
    console.error("Error getting API key:", error);
    return null;
  }
};

/**
 * Get user role from session storage
 * @returns {string|null} User role or null if not found
 */
export const getUserRole = () => {
  try {
    const userLogin = JSON.parse(sessionStorage.getItem("userLogin") || "[]");
    return userLogin.length > 0 ? userLogin[0].role : null;
  } catch (error) {
    console.error("Error getting user role:", error);
    return null;
  }
};

/**
 * Check if user is logged in and has API key
 * @returns {boolean} True if user is logged in with API key
 */
export const hasApiKey = () => {
  return getApiKey() !== null;
};

/**
 * Check if user is admin
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  return getUserRole() === 'admin';
};

/**
 * Create axios config with API key in Authorization header
 * @param {object} additionalConfig - Additional axios config
 * @returns {object} Axios config with Authorization header
 */
export const createAuthConfig = (additionalConfig = {}) => {
  const apiKey = getApiKey();
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...additionalConfig.headers
    },
    ...additionalConfig
  };
  
  // Add API key to Authorization header if available
  if (apiKey) {
    config.headers['Authorization'] = `Bearer ${apiKey}`;
  }
  
  return config;
};

/**
 * Create axios config with API key in X-API-Key header
 * @param {object} additionalConfig - Additional axios config
 * @returns {object} Axios config with X-API-Key header
 */
export const createApiKeyConfig = (additionalConfig = {}) => {
  const apiKey = getApiKey();
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...additionalConfig.headers
    },
    ...additionalConfig
  };
  
  // Add API key to X-API-Key header if available
  if (apiKey) {
    config.headers['X-API-Key'] = apiKey;
  }
  
  return config;
};

/**
 * Legacy function: Add API key to request data (for backward compatibility)
 * @param {object} data - Request data object
 * @returns {object} Request data with API key added
 */
export const addApiKey = (data = {}) => {
  const apiKey = getApiKey();
  return apiKey ? { ...data, apiKey } : data;
};

/**
 * Make authenticated axios request with API key in header
 * @param {object} axios - Axios instance
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {object} data - Request data
 * @param {object} config - Additional axios config
 * @returns {Promise} Axios request promise
 */
export const authenticatedRequest = (axios, method, url, data = {}, config = {}) => {
  const authConfig = createAuthConfig(config);
  
  switch (method.toLowerCase()) {
    case 'get':
      return axios.get(url, authConfig);
    case 'post':
      return axios.post(url, data, authConfig);
    case 'put':
      return axios.put(url, data, authConfig);
    case 'delete':
      return axios.delete(url, authConfig);
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
};